<template>
  <VRow
    v-show="loading"
    no-gutters
    :class="[{
      'overlay': loading && variant !== 'inline',
      'overlay--scrim': loading && variant === 'scrim',
      'loading-overlay--inline': loading && variant === 'inline'
    }, $attrs.class]"
    class="loading-overlay"
    v-bind="$attrs"
  >
    <!--To use add class add-processing to content you wish to fill -->
    <!-- v-if, not a hidden class: an idle spinner is still a full SVG component, and
         list pages render one overlay per card (~100 on a category page), all of
         them server-rendered and hydrated for nothing. -->
    <VProgressCircular
      v-if="loading"
      :size="spinnerSize"
      :width="spinnerWidth"
      :color="spinnerColor"
      :class="{'loading-overlay__spinner--disc': variant === 'scrim'}"
      :aria-label="resolvedLabel"
      indeterminate
    />
  </VRow>
</template>

<script lang="ts" setup>
// Loading is announced to screen readers through the shared region in ~/utils/loadingAnnouncer,
// which holds the rules (threshold, one announcement for overlapping loads). AB#64981
import { ensureLoadingRegion, startLoadingAnnouncement, stopLoadingAnnouncement } from '~/utils/loadingAnnouncer'

defineOptions({
  inheritAttrs: false
})

const { t } = useI18n()

interface Props {
 loading?: boolean
 spinnerSize?: string
 spinnerWidth?: string
 spinnerColor?: string
 /**
  * A translucent, lightly blurred cover instead of opaque white, so the page stays
  * visible behind the spinner. Join steps use it: they keep the previous step on screen
  * while the next one loads, and an opaque cover would hide the step being held. AB#64268
  */
 scrim?: boolean
 /**
  * Announced to screen readers while loading. Defaults to the translated "Loading". When
  * several overlays load at once only one announcement is made, with the label of the earliest
  * one still loading. AB#64981
  */
 label?: string
 /** A centered placeholder in the normal flow instead of the full-cover overlay. Wins over `scrim`. AB#64981 */
 inline?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  scrim: false,
  spinnerSize: '60',
  spinnerWidth: '6',
  spinnerColor: '#888',
  label: undefined,
  inline: false
})

const variant = computed<'cover' | 'scrim' | 'inline'>(() => {
  if (props.inline) {
    return 'inline'
  }
  return props.scrim ? 'scrim' : 'cover'
})

// a11y.loading is in Lokalise; its non-en-US translations are still pending there. AB#64981
const resolvedLabel = computed(() => props.label || t('a11y.loading'))

// Identifies this overlay to the announcer. Client-only: the watcher never fires during SSR,
// and the initial state is reported on mount.
const owner = {}
const report = (loading: boolean) => loading
  ? startLoadingAnnouncement(owner, () => resolvedLabel.value)
  : stopLoadingAnnouncement(owner)

watch(() => props.loading, report)
onMounted(() => {
  ensureLoadingRegion()
  report(props.loading)
})
onBeforeUnmount(() => stopLoadingAnnouncement(owner))
</script>

<style lang="scss" scoped>
.overlay {
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  position: absolute;
  background: #fff;
  justify-content: center;
  align-items: center;
  z-index: 2147483638;
}

.overlay--scrim {
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(2px);
}

.loading-overlay--inline {
  width: 100%;
  justify-content: center;
  align-items: center;
}

// Over a scrim the held page shows through, and dark text or buttons behind the #888
// spinner fall under the 3:1 non-text minimum (WCAG 1.4.11). An opaque white disc keeps
// the spinner at 3.54:1 whatever is underneath. AB#64268
.loading-overlay__spinner--disc {
  box-sizing: content-box;
  padding: 10px;
  border-radius: 50%;
  background: #fff;
}

</style>
